import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useDeviceType from '@travel/traveler-core/hooks/useDeviceType';

import ReviewList from 'pages/ProviderInformationPage/components/ReviewList';
import ReviewScoreInformation from 'pages/ProviderInformationPage/components/ReviewScoreInformation';

import { fetchMoreProvideGuestReview, fetchProvideGuestReview } from 'store/providerReview/actions';
import {
  getIsFetching,
  getIsFetchingMore,
  getProviderGuestReview,
} from 'store/providerReview/selectors';

import {
  DEFAULT_SORTKEY,
  REVIEW_DEFAULT_LIMIT_FOR_MOBILE,
  REVIEW_DEFAULT_LIMIT_FOR_WEB,
} from 'constants/review';
import { Translate } from 'core/translate';
import type { TravelTypeBreakdown } from 'ProviderReview-Types';

import styles from './providerReviews.module.scss';

type Props = {
  className?: string;
  /** Provider ID */
  pageIdentifier: string;
};

function ProviderReviews(props: Props) {
  const { pageIdentifier } = props;

  const isMobile = useDeviceType() === 'sp';
  const dispatch = useDispatch();
  const reviewData = useSelector(getProviderGuestReview);
  const isFetching = useSelector(getIsFetching);
  const isFetchingMore = useSelector(getIsFetchingMore);

  const [selectedSort, setSelectedSort] = useState(DEFAULT_SORTKEY);
  const [selectedFilters, setSelectedFilters] = useState<Array<number>>([]);
  const [selectedTravelType, setSelectedTravelType] = useState<Array<TravelTypeBreakdown>>([]);

  const anchorRef = useRef<HTMLDivElement | null>(null);

  const setSort = useCallback((sort: string) => setSelectedSort(sort), []);
  const setFilters = useCallback((filters: Array<number>) => setSelectedFilters(filters), []);
  const setTravelType = useCallback(
    (travelType: Array<TravelTypeBreakdown>) => setSelectedTravelType(travelType),
    [],
  );

  const REVIEW_DEFAULT_LIMIT = isMobile
    ? REVIEW_DEFAULT_LIMIT_FOR_MOBILE
    : REVIEW_DEFAULT_LIMIT_FOR_WEB;

  const onChangeFilters = useCallback(
    (sort: string, filters: Array<number>, travelType: Array<TravelTypeBreakdown>) => {
      dispatch(
        fetchProvideGuestReview(pageIdentifier, {
          offset: 0,
          limit: REVIEW_DEFAULT_LIMIT,
          sort,
          filters,
          travelType,
        }),
      );
    },
    [REVIEW_DEFAULT_LIMIT, dispatch, pageIdentifier],
  );

  const onChangePage = useCallback(
    (offset: number) => {
      const req = {
        offset,
        limit: REVIEW_DEFAULT_LIMIT,
        sort: selectedSort,
        filters: selectedFilters,
        travelType: selectedTravelType,
      };

      if (isMobile) {
        dispatch(fetchMoreProvideGuestReview(pageIdentifier, req));
      } else {
        dispatch(fetchProvideGuestReview(pageIdentifier, req));
        window.scrollTo({
          top: anchorRef.current?.offsetTop,
          behavior: 'smooth',
        });
      }
    },
    [
      REVIEW_DEFAULT_LIMIT,
      dispatch,
      isMobile,
      pageIdentifier,
      selectedFilters,
      selectedSort,
      selectedTravelType,
    ],
  );

  return (
    <div className={props.className} ref={anchorRef} data-testid="providerReviews-wrapper">
      {!isMobile && (
        <h2>
          <Translate id="Provider_Info.Tablet_PC.Rating_Review" className={styles.title} />
        </h2>
      )}
      <div className={styles.wrapper}>
        <ReviewScoreInformation
          onPage="reviewList"
          setSort={setSort}
          setFilters={setFilters}
          setTravelType={setTravelType}
          onChangeFilters={onChangeFilters}
          reviewData={reviewData}
          selectedFilters={selectedFilters}
          selectedSort={selectedSort}
          selectedTravelType={selectedTravelType}
        />
        <ReviewList
          onChangePage={onChangePage}
          isFetching={isFetching}
          isFetchingMore={isFetchingMore}
          isActionButtonsHidden={true}
          onPage="reviewList"
          setSort={setSort}
          setFilters={setFilters}
          setTravelType={setTravelType}
          onChangeFilters={onChangeFilters}
          reviewData={reviewData}
          selectedFilters={selectedFilters}
          selectedSort={selectedSort}
          selectedTravelType={selectedTravelType}
          {...reviewData}
        />
      </div>
    </div>
  );
}

export default ProviderReviews;
